import { jsx, jsxs } from "react/jsx-runtime";
import { createElement } from "react";
import { IconButton, Tooltip, useThemeProps } from "@mui/material";
import { Children, cloneElement, forwardRef } from "react";
import { useSlotProps } from "../../../base/index.js";
import { MoreVert } from "../../../icons/index.js";
import { editorToolbarName } from "./classes.js";
import { Divider } from "./components/Divider.js";
import { Overflow } from "./components/Overflow.js";
import { Popover } from "./components/Popover.js";
import { Root } from "./components/Root.js";
import { Toolbar } from "./components/Toolbar.js";
import { DividerPlaceholder, useEditorToolbar } from "./hooks/useEditorToolbar.js";
import { useUtilityClasses } from "./hooks/useUtilityClasses.js";
function DefaultShowMore(props) {
  return /* @__PURE__ */ jsx(Tooltip, { title: props.extra?.tooltip, children: /* @__PURE__ */ jsx(IconButton, { onClick: props.extra?.onClick, size: "small", children: /* @__PURE__ */ jsx(MoreVert, {}) }) });
}
const EditorToolbar = forwardRef(function EditorToolbar2(inProps, ref) {
  const props = useThemeProps({
    name: editorToolbarName,
    props: inProps
  });
  const {
    sx,
    testId = `${editorToolbarName}-root`,
    children,
    className = "",
    components,
    componentsProps,
    overflowTooltip
  } = props;
  const {
    //
    open,
    tools,
    onOpen,
    onClose,
    rootRef,
    rAnchor,
    rootWidth,
    setHidden,
    overflowTools
  } = useEditorToolbar(ref);
  const ownerState = props;
  const utilityClasses = useUtilityClasses(ownerState);
  const rootSlotProps = useSlotProps({
    className: `${className} ${utilityClasses.root}`,
    ownerState,
    elementType: Root,
    additionalProps: {
      sx,
      as: components?.Root,
      ref: rootRef,
      "data-tidal": editorToolbarName,
      "data-testid": testId
    },
    externalSlotProps: componentsProps?.root
  });
  const toolbarSlotProps = useSlotProps({
    className: utilityClasses.toolbar,
    ownerState,
    elementType: Toolbar,
    additionalProps: {
      as: components?.Toolbar,
      density: "collapsed",
      ShowMore: DefaultShowMore,
      showMoreProps: { onClick: onOpen, tooltip: overflowTooltip },
      onChangeHiddenItemsCount: setHidden
    },
    externalSlotProps: componentsProps?.toolbar
  });
  const popoverSlotProps = useSlotProps({
    className: utilityClasses.popover,
    ownerState,
    elementType: Popover,
    additionalProps: {
      as: components?.Popover,
      open,
      onClose,
      anchorEl: rAnchor.current,
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "right"
      },
      transformOrigin: {
        vertical: "top",
        horizontal: "right"
      }
    },
    externalSlotProps: componentsProps?.popover
  });
  const overflowSlotProps = useSlotProps({
    className: utilityClasses.overflow,
    ownerState,
    elementType: Overflow,
    additionalProps: {
      as: components?.Overflow,
      toolbarWidth: rootWidth
    },
    externalSlotProps: componentsProps?.overflow
  });
  const dividerSlotProps = useSlotProps({
    className: utilityClasses.divider,
    ownerState,
    elementType: Divider,
    additionalProps: { as: components?.Divider },
    externalSlotProps: componentsProps?.divider
  });
  if (tools.length === 0 && Children.count(children) === 0) return null;
  return /* @__PURE__ */ jsxs(Root, { ...rootSlotProps, children: [
    /* @__PURE__ */ jsx("div", { style: { display: "none", visibility: "hidden" }, children }),
    /* @__PURE__ */ jsx(Toolbar, { ...toolbarSlotProps, children: tools.map(
      (tool, i) => tool.type === DividerPlaceholder ? /* @__PURE__ */ createElement(Divider, { ...dividerSlotProps, key: `divider-${i}` }) : cloneElement(tool, { key: `tool-${i}` })
    ) }),
    /* @__PURE__ */ jsx(Popover, { ...popoverSlotProps, children: /* @__PURE__ */ jsx(Overflow, { ...overflowSlotProps, children: overflowTools.map(
      (tool, i) => tool.type === DividerPlaceholder ? /* @__PURE__ */ createElement(Divider, { ...dividerSlotProps, key: `divider-${i}` }) : cloneElement(tool, { key: `overflow-${i}` })
    ) }) })
  ] });
});
export {
  EditorToolbar
};
//# sourceMappingURL=EditorToolbar.js.map
